import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getInstanceByDom } from 'echarts';
import Panel from '../panel/Panel';
import './Layout.scss';
import MobileMenu from '../mobile-menu/MobileMenu';
import MobileTabBar from '../mobile-tab-bar/MobileTabBar';
import FirstProjectOnboarding from '../onboarding-popup/FirstProjectOnboarding';
import AdsAnnouncementPopup from '../ads-announcement/AdsAnnouncementPopup';
import InsightsAnnouncementPopup from '../insights-announcement/InsightsAnnouncementPopup';
import VideoTutorial from '../onboarding-video/VideoTutorial';
import TrialBar from '../../trial-bar/TrialBar';
import ImpersonationBanner from '../../impersonation-banner/ImpersonationBanner';
import VersionBanner from '../../version-banner/VersionBanner';
import useBilling from '../../../hooks/account/UseBilling';
import { useAccount } from '../../../contexts/account/AccountContexts';
import useIsMobile from '../../../hooks/global/UseIsMobile';
import SovSimulatorDialog from '../sov-simulator/SovSimulatorDialog';

interface LayoutProps {
  children: React.ReactNode;
  dataPage?: 'faq' | 'trial-ended' | 'integrations' | 'geo-audit' | '';
}

const PANEL_COLLAPSED_KEY = 'chatbeat-panel-collapsed';

const Layout: React.FC<LayoutProps> = ({ children, dataPage = '' }) => {
  const { accountResponse } = useAccount();
  const canShowBilling = useBilling();
  const isMobile = useIsMobile();

  // Czytane w inicjalizatorze (a nie w useEffect), żeby pierwszy paint miał już właściwą szerokość
  // kolumny — inaczej panel mignąłby rozwinięty i skoczył.
  const [isPanelCollapsed, setPanelCollapsed] = useState<boolean>(
    () => localStorage.getItem(PANEL_COLLAPSED_KEY) === 'true'
  );

  const togglePanel = useCallback(() => {
    setPanelCollapsed((collapsed) => {
      const next = !collapsed;
      localStorage.setItem(PANEL_COLLAPSED_KEY, String(next));
      return next;
    });
  }, []);

  const isFirstRender = useRef(true);

  // `data-panel-collapsed` to znacznik dla stylów, które nie mogą sięgnąć do `.panel--small`
  // selektorem — tooltipy pozycji menu renderują się przez `appendTo={document.body}`, więc panel nie
  // jest ich przodkiem. `data-panel-animating` żyje tylko przez czas przejścia i pozwala odróżnić
  // przełączenie panelu od zwykłego wejścia na stronę (etykiety mają przenikać w pierwszym przypadku,
  // a nie migotać przy każdym załadowaniu).
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-panel-collapsed', String(isPanelCollapsed));

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    root.setAttribute('data-panel-animating', 'true');
    const timeout = setTimeout(() => root.removeAttribute('data-panel-animating'), 300);

    return () => clearTimeout(timeout);
  }, [isPanelCollapsed]);

  const contentRef = useRef<HTMLDivElement>(null);

  // Zwinięcie panelu zwęża kolumnę treści bez zdarzenia `resize` okna, a wykresy ECharts rysują się na
  // canvasie o rozmiarze ustalonym przy poprzednim renderze — zostają w starej szerokości i wychodzą
  // poza layout. Same pilnują rozmiaru ResizeObserverem na swoim kontenerze, ale ten nie zawsze się
  // zwęża (np. gdy wykres siedzi w SimpleBarze, który zamiast tego pokazuje poziomy scroll).
  const resizeCharts = useCallback(() => {
    contentRef.current?.querySelectorAll<HTMLElement>('[_echarts_instance_]').forEach((element) => {
      getInstanceByDom(element)?.resize();
    });
  }, []);

  // Obserwujemy szerokość samej kolumny zamiast wiązać się z przebiegiem animacji: obserwator odzywa
  // się na każdej klatce przejścia (więc wykres rośnie razem z kolumną, bez doskoku na końcu), a przy
  // okazji łapie każdą inną zmianę szerokości — zmianę rozmiaru okna czy pojawienie się scrollbara.
  useEffect(() => {
    const content = contentRef.current;
    if (!content) {
      return;
    }

    const observer = new ResizeObserver(() => resizeCharts());
    observer.observe(content);

    return () => observer.disconnect();
  }, [resizeCharts]);

  // Telefon: dymek wykresu otwiera stuknięcie i zostaje, bo nie ma „zjechania kursorem” — a że jest
  // doklejony do `body` (`appendToBody`), wisiał nad treścią także po przewinięciu. Chowamy dymki
  // wszystkich wykresów przy przewijaniu i przy stuknięciu poza wykres.
  useEffect(() => {
    if (!isMobile) {
      return;
    }

    const hideChartTooltips = () => {
      document.querySelectorAll<HTMLElement>('[_echarts_instance_]').forEach((element) => {
        getInstanceByDom(element)?.dispatchAction({ type: 'hideTip' });
      });
    };
    const onTouchStart = (event: TouchEvent) => {
      if (!(event.target as HTMLElement | null)?.closest('[_echarts_instance_]')) {
        hideChartTooltips();
      }
    };

    // Capture: przewija się kolumna treści, a nie okno, więc zdarzenie nie dociera do `document`.
    document.addEventListener('scroll', hideChartTooltips, { capture: true, passive: true });
    document.addEventListener('touchstart', onTouchStart, { capture: true, passive: true });
    return () => {
      document.removeEventListener('scroll', hideChartTooltips, { capture: true });
      document.removeEventListener('touchstart', onTouchStart, { capture: true });
    };
  }, [isMobile]);

  const isGradientPage =
    dataPage === 'faq' ||
    dataPage === 'trial-ended' ||
    dataPage === 'integrations' ||
    dataPage === 'geo-audit';

  return (
    <div className="layout">
      {/* Onboarding i ogłoszenia mówią „załóż swój pierwszy projekt" / „mamy nową funkcję" — gość
          jest widzem na cudzym koncie, a zamknięcie popupu zapisuje się właścicielowi konta. */}
      {accountResponse?.role !== 'GUEST' && (
        <>
          <FirstProjectOnboarding />
          <AdsAnnouncementPopup />
          <InsightsAnnouncementPopup />
        </>
      )}
      <VideoTutorial />
      <ImpersonationBanner />
      <VersionBanner />
      <div className="layout__page">
        <div className={`layout__left ${isPanelCollapsed ? 'layout__left--small' : ''}`}>
          {/* Na telefonie panel żyje w szufladzie `MobileMenu` — drugi, ukryty egzemplarz dublowałby id
              w DOM i zapytania. */}
          {!isMobile && <Panel isCollapsed={isPanelCollapsed} onToggleCollapse={togglePanel} />}
        </div>
        <div
          ref={contentRef}
          className={`layout__right ${isGradientPage && 'layout__right--gradient'} ${
            isPanelCollapsed ? 'layout__right--small' : ''
          }`}
          data-page={dataPage}
        >
          <TrialBar position="top" />
          <div
            className={`layout__mobile ${
              !accountResponse ||
              accountResponse.accountStatus === 'PAID' ||
              !canShowBilling ||
              (accountResponse.accountStatus === 'TRIAL' && !accountResponse?.showTrialNotification)
                ? ''
                : 'layout__mobile--bar'
            }`}
          >
            <MobileMenu />
          </div>
          <main>{children}</main>
          <MobileTabBar />
          <SovSimulatorDialog />
        </div>
      </div>
    </div>
  );
};

export default Layout;
